import { UnreachableCaseError } from 'ts-essentials';
import { AbstractLogger } from './abstract-logger';
import { ILogger, LogLevel, LoggerType } from './logger.types';

/**
 * Capture original console methods at module load time.
 * This prevents infinite loops if console is later overridden/proxied.
 */
const originalConsole = {
  debug: console.debug.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
} as const;

export class ConsoleLogger extends AbstractLogger {
  public override get type(): LoggerType {
    return 'console';
  }

  constructor(name = 'console', logLevel: LogLevel) {
    super(name, logLevel);
  }

  public createNamedLogger(name: string): ILogger {
    return new ConsoleLogger(name, this.level);
  }

  public setProjectContext(): void {
    // No-op
  }

  protected log(
    level: LogLevel,
    message: string,
    context?: object,
    error?: Error
  ): void {
    if (!this.shouldLog(level)) return;

    const loggerMessage = `[${this.name}]: ${message}`;
    const args = [context, error].filter((a) => a !== undefined && a !== null);

    switch (level) {
      case LogLevel.Debug:
        return originalConsole.debug(
          `%c${loggerMessage}`,
          'color: dodgerblue;',
          ...args
        );

      case LogLevel.Info:
        return originalConsole.info(
          `%c${loggerMessage}`,
          'color: seagreen;',
          ...args
        );

      case LogLevel.Warn:
        return originalConsole.warn(loggerMessage, ...args);

      case LogLevel.Error:
        return originalConsole.error(loggerMessage, ...args);

      default:
        throw new UnreachableCaseError(level);
    }
  }
}
